﻿import React, { useMemo, useState, useCallback } from "react";
import {
    Fabric,
    IColumn,
    SearchBox,
    DetailsList,
    SelectionMode,
    IDetailsHeaderProps,
    DetailsListLayoutMode,
    ConstrainMode,
    IRenderFunction,
    TooltipHost,
    DirectionalHint,
    DetailsRow,
    IDetailsRowProps,
    StickyPositionType,
    DetailsHeader,
    Sticky,
    ScrollbarVisibility,
    ScrollablePane
} from "@fluentui/react";
import { getDateToDisplay } from "../../util_glob";
import { dpaSearchStyles, orderHeaderCustomStyles, scrollablePaneShortStyles } from "../../styles_glob";
import styles from "./JsonListTable.module.css";

{/*
    JsonListTable
    View-only table for 2-level JSON lists (originally CSV data).
    - Fields are dynamic but consistent within a given dataset.
    - Supports global search and per-column sorting.
    - Different data shapes can be supplied in different cases; columns are derived
      from the union of keys present across all rows.
*/}

interface JsonListTableProps {
    // Accepts a JSON string, an array of flat objects, or a single object.
    data?: string | any[] | Record<string, any>;
    title?: string;
    emptyText?: string;
    fid?: string;
    ext_stamp?: Date;
}

type Row = Record<string, any>;

const prettifyHeader = (key: string): string =>
    key
        .replace(/[_\-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();

const stringifyCell = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
        try {
            return JSON.stringify(val);
        } catch {
            return String(val);
        }
    }
    return String(val);
};

// Map a single array entry to a table row. Objects become rows as-is; primitives are wrapped under a "value" column.
const entryToRow = (r: any): Row => (r !== null && typeof r === "object" && !Array.isArray(r) ? r : { value: r });
const onRenderDetailsHeaderJT = (props: IDetailsHeaderProps, defaultRender?: (props: IDetailsHeaderProps) => JSX.Element) => {
    return (
        <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
            <DetailsHeader
                {...props}
                styles={orderHeaderCustomStyles}
            />
        </Sticky>
    );
};


// Normalize incoming data into an array of flat objects (rows). Primary shape: an array of objects, e.g. [ { prop1: "a", prop2: "b" }, { prop1: "c", prop2: "d" } ]
const normalizeToRows = (data: JsonListTableProps["data"]): Row[] => {
    if (data === null || data === undefined) return [];
    let parsed: any = data;
    if (typeof data === "string") {
        const trimmed = data.trim();
        if (trimmed === "") return [];
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return [];
        }
    }
    // ✅ main case: array of objects → one row per object
    if (Array.isArray(parsed)) {
        return parsed.filter(r => r !== null && r !== undefined).map(entryToRow);
    }
    if (typeof parsed === "object") {
        // If the object holds a single array property, use that array as the row source.
        const arrayProps = Object.values(parsed).filter(v => Array.isArray(v)) as any[][];
        if (arrayProps.length === 1) {
            return arrayProps[0].filter(r => r !== null && r !== undefined).map(entryToRow);
        }
        // Otherwise treat the object itself as a single row.
        return [parsed];
    }
    return [{ value: parsed }];
};

// Derive the ordered union of keys across all rows (keeps field order consistent).
const deriveKeys = (rows: Row[]): string[] => {
    const seen = new Set<string>();
    const keys: string[] = [];
    for (const row of rows) {
        if (row && typeof row === "object" && !Array.isArray(row)) {
            for (const k of Object.keys(row)) {
                if (!seen.has(k)) {
                    seen.add(k);
                    keys.push(k);
                }
            }
        }
    }
    return keys;
};

const compareValues = (a: any, b: any): number => {
    const av = a === null || a === undefined ? "" : a;
    const bv = b === null || b === undefined ? "" : b;
    const an = typeof av === "number" ? av : Number(av);
    const bn = typeof bv === "number" ? bv : Number(bv);
    if (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "") return an - bn;
    return stringifyCell(av).localeCompare(stringifyCell(bv), undefined, { numeric: true, sensitivity: "base" });
};

const JsonListTable: React.FC<JsonListTableProps> = ({ data, title, emptyText = "No data to display", fid=undefined, ext_stamp=new Date() }) => {
    const allRows = useMemo(() => normalizeToRows(data), [data]);
    const keys    = useMemo(() => deriveKeys(allRows), [allRows]);

    const [searchText, setSearchText] = useState("");
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDescending, setSortDescending] = useState(false);

    const onColumnClick = useCallback(
        (_ev: React.MouseEvent<HTMLElement> | undefined, column: IColumn) => {
            setSortKey(prevKey => {
                if (prevKey === column.key) {
                    setSortDescending(prev => !prev);
                    return prevKey;
                }
                setSortDescending(false);
                return column.key;
            });
        },
        []
    );

    const filteredRows = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        if (!term) return allRows;
        return allRows.filter(row => keys.some(k => stringifyCell(row[k]).toLowerCase().includes(term)));
    }, [allRows, keys, searchText]);

    const sortedRows = useMemo(() => {
        if (!sortKey) return filteredRows;
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const res = compareValues(a[sortKey], b[sortKey]);
            return sortDescending ? -res : res;
        });
        return copy;
    }, [filteredRows, sortKey, sortDescending]);

    const columns: IColumn[] = useMemo(
        () =>
            keys.map(k => ({
                key: k,
                name: prettifyHeader(k),
                fieldName: k,
                minWidth: 80,
                maxWidth: 320,
                isResizable: true,
                isSorted: sortKey === k,
                isSortedDescending: sortKey === k ? sortDescending : false,
                sortAscendingAriaLabel: "Sorted A to Z",
                sortDescendingAriaLabel: "Sorted Z to A",
                onColumnClick: onColumnClick,
                data: "string",
                isPadded: true,
                onRender: (item: Row) => {
                    const txt = stringifyCell(item[k]);
                    return (
                        <div className={styles.cellText} title={txt}>
                            {txt}
                        </div>
                    );
                }
            })),
        [keys, sortKey, sortDescending, onColumnClick]
    );

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = useCallback((props, defaultRender) => {
        if (!props || !defaultRender) return null;
        return defaultRender({
            ...props,
            styles: {
                root: {
                    paddingTop: 0,
                    selectors: {
                        ".ms-DetailsHeader-cell": {
                            fontFamily: 'Urbanist, Raleway, "Roboto", sans-serif',
                            color: "#2F4858"
                        }
                    }
                }
            }
        });
    }, []);

    return (
        <Fabric className={styles.wrapper}>
            <div className={styles.headerBar}>
                <div className={styles.titleAndCount}>
                    <div className={styles.fidBadge}>{fid}</div>
                    {title && <div className={styles.title}>{title}</div>}
                    <TooltipHost content="Number of rows" directionalHint={DirectionalHint.topCenter}>
                        <div className={styles.countBadge}><div className={styles.countLabel}>Count:</div> <span>{sortedRows.length}</span></div>
                    </TooltipHost>
                </div>
                <SearchBox
                    placeholder="Search..."
                    value={searchText}
                    onChange={(_, v) => setSearchText(v || "")}
                    onClear={() => setSearchText("")}
                    styles={dpaSearchStyles}
                />
            </div>

            {sortedRows.length > 0 && columns.length > 0 ? (
                <div className={styles.listArea}>
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={ scrollablePaneShortStyles} >
                        <DetailsList
                            className={styles.list}
                            items={sortedRows}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            layoutMode={DetailsListLayoutMode.justified}
                            constrainMode={ConstrainMode.unconstrained}
                            isHeaderVisible={true}
                            onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeaderJT(props!)}
                            setKey="jsonListTable"
                            compact={false}

                        />
                    </ScrollablePane>
                </div>
            ) : (
                <div className={styles.emptyState}>{emptyText}</div>
            )}

            <div className={styles.footerWrapper}>
                <div className={styles.dataListModalFooter}><div className={styles.footLabel}>Sync Date:</div>{ext_stamp ? `${getDateToDisplay(ext_stamp)}` : 'No Sync Date'}</div>
            </div>
        </Fabric>
    );
};

export default JsonListTable;
